// Phase 1024 — Minimal dedicated shell for the BUILDING_PAYMENTS_CLERK role.
//
// The clerk has NO sidebar, NO financial dashboards, NO admin tools. The
// shell shows a building list, drills into apartments (number + outstanding
// amount only), and lets the clerk record / update payments.
//
// Server-side middleware already enforces the role scope — this file only
// handles the user-facing UX for the one role that has it.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Buildings, SignOut, ArrowLeft, Plus, Pencil, Lock } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtEuro(n) {
  const v = Number(n || 0);
  return v.toLocaleString('el-GR', { style: 'currency', currency: 'EUR' });
}

export default function ClerkPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [apartments, setApartments] = useState([]);
  const [buildingPayments, setBuildingPayments] = useState([]);
  const [aptPayments, setAptPayments] = useState({});
  const [selectedApt, setSelectedApt] = useState(null);

  const [showPayModal, setShowPayModal] = useState(false);
  const [payTarget, setPayTarget] = useState(null); // {kind:'building'|'apartment', id, apt?, existing?}
  const [payForm, setPayForm] = useState({ amount: '', date: '', notes: '', reference: '' });

  const loadBuildings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/clerk/buildings`, { withCredentials: true });
      setBuildings(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load buildings');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBuildingDetail = useCallback(async (bld) => {
    setSelectedBuilding(bld);
    setSelectedApt(null);
    setApartments([]);
    setBuildingPayments([]);
    try {
      const [aptsResp, paysResp] = await Promise.all([
        axios.get(`${API}/clerk/buildings/${bld.building_id}/apartments`, { withCredentials: true }),
        axios.get(`${API}/clerk/buildings/${bld.building_id}/payments`, { withCredentials: true }),
      ]);
      setApartments(aptsResp.data?.apartments || []);
      setBuildingPayments(paysResp.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load building');
    }
  }, []);

  const loadAptPayments = useCallback(async (apt) => {
    setSelectedApt(apt);
    try {
      const { data } = await axios.get(`${API}/clerk/apartments/${apt.apartment_id}/payments`, { withCredentials: true });
      setAptPayments({ [apt.apartment_id]: data?.payments || [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load apartment payments');
    }
  }, []);

  useEffect(() => { loadBuildings(); }, [loadBuildings]);

  const openCreateBuildingPay = () => {
    setPayTarget({ kind: 'building', id: selectedBuilding.building_id });
    setPayForm({ amount: '', date: new Date().toISOString().slice(0, 10), notes: '', reference: '' });
    setShowPayModal(true);
  };
  const openEditBuildingPay = (p) => {
    setPayTarget({ kind: 'building', id: selectedBuilding.building_id, existing: p });
    setPayForm({ amount: String(p.amount ?? ''), date: p.date || '', notes: p.notes || '', reference: p.reference || '' });
    setShowPayModal(true);
  };
  const openCreateAptPay = () => {
    setPayTarget({ kind: 'apartment', id: selectedApt.apartment_id });
    setPayForm({ amount: '', date: new Date().toISOString().slice(0, 10), notes: '', reference: '' });
    setShowPayModal(true);
  };
  const openEditAptPay = (p) => {
    setPayTarget({ kind: 'apartment', id: selectedApt.apartment_id, existing: p });
    setPayForm({ amount: String(p.amount ?? ''), date: p.date || '', notes: p.notes || '', reference: p.reference || '' });
    setShowPayModal(true);
  };

  const submitPayment = async () => {
    const amount = parseFloat(payForm.amount);
    if (!amount || amount <= 0) { toast.error('Amount must be positive'); return; }
    const body = { amount, date: payForm.date, notes: payForm.notes, reference: payForm.reference };
    try {
      if (payTarget.kind === 'building') {
        if (payTarget.existing) {
          await axios.put(`${API}/clerk/buildings/${payTarget.id}/payments/${payTarget.existing.payment_id}`, body, { withCredentials: true });
        } else {
          await axios.post(`${API}/clerk/buildings/${payTarget.id}/payments`, body, { withCredentials: true });
        }
        await loadBuildingDetail(selectedBuilding);
      } else {
        if (payTarget.existing) {
          await axios.put(`${API}/clerk/apartment-payments/${payTarget.existing.payment_id}`, body, { withCredentials: true });
        } else {
          await axios.post(`${API}/clerk/apartments/${payTarget.id}/payments`, body, { withCredentials: true });
        }
        await loadAptPayments(selectedApt);
        await loadBuildingDetail(selectedBuilding);
      }
      toast.success('Payment saved');
      setShowPayModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-zinc-950 text-white px-6 py-3 flex items-center justify-between" data-testid="clerk-header">
        <div className="flex items-center gap-3">
          <Lock size={20} />
          <div>
            <div className="text-sm font-semibold" data-testid="clerk-header-title">CleanPro — Building Payments</div>
            <div className="text-[11px] text-zinc-400">Restricted role · {user?.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="text-white hover:bg-zinc-800"
                  onClick={() => navigate('/account/change-password')} data-testid="clerk-change-pw-btn">
            Password
          </Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-zinc-800" onClick={handleLogout} data-testid="clerk-logout-btn">
            <SignOut size={14} className="mr-1" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6">
        {!selectedBuilding && (
          <>
            <h1 className="text-xl font-semibold mb-4 flex items-center gap-2" data-testid="clerk-buildings-title">
              <Buildings size={22} /> Buildings
            </h1>
            {loading && <div className="text-sm text-zinc-500">Loading…</div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {buildings.map(b => (
                <button key={b.building_id}
                        onClick={() => loadBuildingDetail(b)}
                        className="text-left bg-white border border-zinc-200 hover:border-zinc-400 p-4 transition-colors"
                        data-testid={`clerk-building-${b.building_id}`}>
                  <div className="font-medium text-zinc-900">{b.name}</div>
                  <div className="text-[11px] text-zinc-400 mt-1">Click to open</div>
                </button>
              ))}
              {!loading && buildings.length === 0 && (
                <div className="text-sm text-zinc-500">No buildings available.</div>
              )}
            </div>
          </>
        )}

        {selectedBuilding && !selectedApt && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <button onClick={() => { setSelectedBuilding(null); }} className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1" data-testid="clerk-back-buildings">
                  <ArrowLeft size={14} /> Buildings
                </button>
                <h1 className="text-xl font-semibold mt-1" data-testid="clerk-building-name">{selectedBuilding.name}</h1>
              </div>
              <Button onClick={openCreateBuildingPay} className="bg-zinc-950 hover:bg-zinc-800 rounded-none" data-testid="clerk-new-bld-pay">
                <Plus size={14} className="mr-1" /> New Building Payment
              </Button>
            </div>

            <section className="mb-6">
              <h2 className="text-sm font-semibold text-zinc-700 mb-2">Apartments</h2>
              <div className="bg-white border border-zinc-200">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase">
                    <tr>
                      <th className="text-left p-2">Apt #</th>
                      <th className="text-right p-2">Outstanding</th>
                      <th className="text-right p-2">Credit</th>
                      <th className="text-right p-2">Net</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {apartments.map(a => (
                      <tr key={a.apartment_id} className="border-t border-zinc-100 hover:bg-zinc-50">
                        <td className="p-2 font-medium">{a.apartment_number}</td>
                        <td className="p-2 text-right">{fmtEuro(a.outstanding)}</td>
                        <td className="p-2 text-right text-emerald-700">{fmtEuro(a.credit_balance)}</td>
                        <td className="p-2 text-right font-semibold">{fmtEuro(a.net_outstanding)}</td>
                        <td className="p-2 text-right">
                          <Button variant="outline" size="sm" className="rounded-none" onClick={() => loadAptPayments(a)} data-testid={`clerk-open-apt-${a.apartment_id}`}>Open</Button>
                        </td>
                      </tr>
                    ))}
                    {apartments.length === 0 && (
                      <tr><td colSpan={5} className="p-4 text-center text-zinc-500">No apartments</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-sm font-semibold text-zinc-700 mb-2">Recent Building Payments</h2>
              <div className="bg-white border border-zinc-200">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase">
                    <tr>
                      <th className="text-left p-2">Date</th>
                      <th className="text-right p-2">Amount</th>
                      <th className="text-left p-2">Ref</th>
                      <th className="text-left p-2">Notes</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {buildingPayments.map(p => (
                      <tr key={p.payment_id} className="border-t border-zinc-100">
                        <td className="p-2">{p.date}</td>
                        <td className="p-2 text-right">{fmtEuro(p.amount)}</td>
                        <td className="p-2">{p.reference || '-'}</td>
                        <td className="p-2 text-zinc-600 truncate max-w-xs">{p.notes}</td>
                        <td className="p-2 text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEditBuildingPay(p)} data-testid={`clerk-edit-bldpay-${p.payment_id}`}><Pencil size={14}/></Button>
                        </td>
                      </tr>
                    ))}
                    {buildingPayments.length === 0 && (
                      <tr><td colSpan={5} className="p-4 text-center text-zinc-500">No payments yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        {selectedBuilding && selectedApt && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <button onClick={() => setSelectedApt(null)} className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1" data-testid="clerk-back-apts">
                  <ArrowLeft size={14} /> Back to {selectedBuilding.name}
                </button>
                <h1 className="text-xl font-semibold mt-1">Apartment {selectedApt.apartment_number}</h1>
                <div className="text-xs text-zinc-500">
                  Outstanding: <span className="font-semibold">{fmtEuro(selectedApt.outstanding)}</span>
                  &nbsp;·&nbsp;Credit: <span className="text-emerald-700">{fmtEuro(selectedApt.credit_balance)}</span>
                  &nbsp;·&nbsp;Net: <span className="font-semibold">{fmtEuro(selectedApt.net_outstanding)}</span>
                </div>
              </div>
              <Button onClick={openCreateAptPay} className="bg-zinc-950 hover:bg-zinc-800 rounded-none" data-testid="clerk-new-apt-pay">
                <Plus size={14} className="mr-1" /> New Payment
              </Button>
            </div>
            <div className="bg-white border border-zinc-200">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase">
                  <tr>
                    <th className="text-left p-2">Date</th>
                    <th className="text-right p-2">Amount</th>
                    <th className="text-left p-2">Ref</th>
                    <th className="text-left p-2">Notes</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(aptPayments[selectedApt.apartment_id] || []).map(p => (
                    <tr key={p.payment_id} className="border-t border-zinc-100">
                      <td className="p-2">{p.date}</td>
                      <td className="p-2 text-right">{fmtEuro(p.amount)}</td>
                      <td className="p-2">{p.reference || '-'}</td>
                      <td className="p-2 text-zinc-600 truncate max-w-xs">{p.notes}</td>
                      <td className="p-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEditAptPay(p)} data-testid={`clerk-edit-aptpay-${p.payment_id}`}><Pencil size={14}/></Button>
                      </td>
                    </tr>
                  ))}
                  {(!aptPayments[selectedApt.apartment_id] || aptPayments[selectedApt.apartment_id].length === 0) && (
                    <tr><td colSpan={5} className="p-4 text-center text-zinc-500">No payments yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>

      <Dialog open={showPayModal} onOpenChange={setShowPayModal}>
        <DialogContent className="max-w-md" data-testid="clerk-pay-modal">
          <DialogHeader>
            <DialogTitle>
              {payTarget?.existing ? 'Update Payment' : 'New Payment'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount (€) *</Label>
              <Input type="number" step="0.01" value={payForm.amount}
                     onChange={e => setPayForm({ ...payForm, amount: e.target.value })}
                     data-testid="clerk-pay-amount" />
            </div>
            <div>
              <Label>Date *</Label>
              <Input type="date" value={payForm.date}
                     onChange={e => setPayForm({ ...payForm, date: e.target.value })}
                     data-testid="clerk-pay-date" />
            </div>
            <div>
              <Label>Reference</Label>
              <Input value={payForm.reference}
                     onChange={e => setPayForm({ ...payForm, reference: e.target.value })}
                     data-testid="clerk-pay-ref" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={payForm.notes}
                     onChange={e => setPayForm({ ...payForm, notes: e.target.value })}
                     data-testid="clerk-pay-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowPayModal(false)}>Cancel</Button>
            <Button className="rounded-none bg-zinc-950 hover:bg-zinc-800" onClick={submitPayment} data-testid="clerk-pay-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
